import React from "react";
import { evaluate } from "feelin";
import { ResponseValue } from "./types";

type InitialValues = {
  IV_joint: string;
  IV_ailment: string;
  IV_market: string;
};

/**
 * Hook to evaluate FEEL expressions for visibility and requirement conditions
 *
 * @param responses - Map of question IDs to their response values
 * @param semanticKeyMap - Map of question IDs to their semantic keys
 * @param testMode - Whether test mode is enabled (evaluates predicates when true)
 * @param initialValues - Initial values (IV_joint, IV_ailment, IV_market) to include in the context
 * @returns Object with evaluatePredicate function
 */
export const useFeelEvaluator = (
  responses: Record<string, ResponseValue>,
  semanticKeyMap: Record<string, string>,
  testMode: boolean,
  initialValues?: InitialValues
) => {
  const buildContext = React.useCallback(() => {
    const context: Record<string, ResponseValue> = {};

    // Add initial values to context
    if (initialValues) {
      Object.entries(initialValues).forEach(([key, value]) => {
        if (value) {
          context[key] = value;
        }
      });
    }

    // Map responses by semanticKey instead of questionId
    Object.entries(semanticKeyMap).forEach(([questionId, semanticKey]) => {
      if (semanticKey && responses[questionId] !== undefined) {
        context[semanticKey] = responses[questionId];
      }
    });

    return context;
  }, [responses, semanticKeyMap, initialValues]);

  const evaluatePredicate = React.useCallback(
    (predicate: string): boolean => {
      if (!testMode) return true; // In non-test mode, always return true (show/optional)
      if (!predicate) return true;

      try {
        const context = buildContext();
        const result = evaluate(predicate, context);

        // Handle the result - feelin returns { value, warnings }
        if (result && typeof result === "object" && "value" in result) {
          const value = result.value;
          if (typeof value === "boolean") {
            return value;
          }
          // If value is not boolean, try to coerce
          return Boolean(value);
        }

        // Fallback for unexpected result format
        return Boolean(result);
      } catch (error) {
        console.error("FEEL evaluation error:", error, "predicate:", predicate);
        console.error("Context was:", buildContext());
        return true; // Default to visible/optional on error
      }
    },
    [testMode, buildContext]
  );

  return { evaluatePredicate };
};
